import { CustomRewardLogType } from "@joonapp/web-shared"
import { useQuery } from "@tanstack/react-query"
import dayjs from "dayjs"

import {
  getCustomRewardBalances,
  getCustomRewardLogsStats,
  getCustomRewards,
  getOnboardingRewardTemplates,
  getParentShop,
  getRewardTemplates,
  getScreenTimeBalances,
  getScreenTimeCurrentException,
  getScreenTimeManualException,
  getScreenTimeScheduledExceptions,
} from "./api"
import { getCustomRewardLogs } from "./api"
import { QUERY_KEYS } from "../../constants"
import { useFilterRewardLogsModalStore } from "../../pages/rewards/hooks/useFilterRewardLogsModalStore"
import { FamilyQuerySelect, RewardsTodayType } from "../../types"
import { getTodaysBonusRewards } from "../bonusRewards"
import { useFamilyQuery, useUserQuery } from "../queries"

export const useCustomRewardLogsQuery = (params?: {
  rewardId?: number
  childId?: number
  type?: CustomRewardLogType
  enabled?: boolean
}) => {
  const { user } = useUserQuery()
  const { rewardId, childId, type, enabled = true } = params || {}
  const { selectedUser, startDate, endDate } = useFilterRewardLogsModalStore()

  const userId = childId || Number(selectedUser?.value) || undefined
  const minDate = startDate ? dayjs(startDate).format("YYYY-MM-DD") : undefined
  const maxDate = endDate ? dayjs(endDate).format("YYYY-MM-DD") : undefined

  return useQuery({
    queryKey: [
      QUERY_KEYS.CUSTOM_REWARD_LOGS,
      rewardId,
      userId,
      minDate,
      maxDate,
      type,
    ],
    queryFn: () =>
      getCustomRewardLogs({ rewardId, userId, minDate, maxDate, type }),
    enabled: !!user && enabled,
  })
}

export const useCustomRewardLogsStatsQuery = (params?: {
  rewardId?: number
}) => {
  const { user } = useUserQuery()
  const { rewardId } = params || {}
  return useQuery({
    queryKey: [QUERY_KEYS.CUSTOM_REWARD_LOGS_STATS, String(rewardId)],
    queryFn: () => getCustomRewardLogsStats({ rewardId: rewardId }),
    enabled: !!user,
  })
}

export const useCustomRewardBalancesQuery = (params?: {
  rewardId?: number
}) => {
  const { user } = useUserQuery()
  const { rewardId } = params || {}
  return useQuery({
    queryKey: [QUERY_KEYS.CUSTOM_REWARD_BALANCES],
    queryFn: () => getCustomRewardBalances(),
    select: (data) =>
      rewardId
        ? data.filter((balance) => balance.reward.id === rewardId)
        : data,
    enabled: !!user,
  })
}

export const useScreenTimeBalancesQuery = (params?: { rewardId?: number }) => {
  const { user } = useUserQuery()
  const { rewardId } = params || {}
  return useQuery({
    queryKey: [QUERY_KEYS.SCREEN_TIME_BALANCES],
    queryFn: () => getScreenTimeBalances(),
    select: (data) =>
      rewardId
        ? data.filter((balance) => balance.reward.id === rewardId)
        : data,
    enabled: !!user,
  })
}

export const useCustomRewardsQuery = () => {
  const { user } = useUserQuery()
  return useQuery({
    queryKey: [QUERY_KEYS.CUSTOM_REWARDS],
    queryFn: getCustomRewards,
    enabled: !!user,
  })
}

export const useRewardTemplatesQuery = () => {
  const { user } = useUserQuery()
  return useQuery({
    queryKey: [QUERY_KEYS.REWARD_TEMPLATES],
    queryFn: getRewardTemplates,
    enabled: !!user,
  })
}

export const useOnboardingRewardTemplatesQuery = () => {
  const { user } = useUserQuery()
  return useQuery({
    queryKey: [QUERY_KEYS.ONBOARDING_REWARD_TEMPLATES],
    queryFn: getOnboardingRewardTemplates,
    enabled: !!user,
  })
}

export const useParentShopQuery = () => {
  const { user } = useUserQuery()
  return useQuery({
    queryKey: [QUERY_KEYS.PARENT_SHOP],
    queryFn: () =>
      getParentShop({ userId: user?.id as number, shopType: "parent_shop" }),
    enabled: !!user,
  })
}

export const useScreenTimeCurrentExceptionQuery = () => {
  const { data: children } = useFamilyQuery(FamilyQuerySelect.CHILDREN)
  return useQuery({
    queryKey: [QUERY_KEYS.SCREEN_TIME_CURRENT_EXCEPTION],
    queryFn: async () => {
      const exceptionMap: Record<number, string> = {}
      if (!children) return exceptionMap
      const results = await Promise.all(
        children.map(async (child) => {
          const exception = await getScreenTimeCurrentException({
            userId: child.user.id,
          })
          return { userId: child.user.id, exception }
        })
      )
      results.forEach((result) => {
        if (!!result.exception) {
          exceptionMap[result.userId] = result.exception
        }
      })
      return exceptionMap
    },
    enabled: !!children,
  })
}

export const useScreenTimeScheduledExceptionsQuery = ({
  userId,
}: {
  userId: number | null
}) => {
  return useQuery({
    queryKey: [QUERY_KEYS.SCREEN_TIME_SCHEDULED_EXCEPTIONS, userId],
    queryFn: () => getScreenTimeScheduledExceptions({ userId }),
    enabled: !!userId,
  })
}

export const useScreenTimeManualExceptionsQuery = ({
  userId,
}: {
  userId: number | null
}) => {
  return useQuery({
    queryKey: [QUERY_KEYS.SCREEN_TIME_MANUAL_EXCEPTIONS, userId],
    queryFn: () => getScreenTimeManualException({ userId }),
    enabled: !!userId,
  })
}

export const useTodaysBonusRewardsQuery = (type: RewardsTodayType) => {
  const { data: family } = useFamilyQuery()
  return useQuery({
    queryKey: [QUERY_KEYS.BONUS_REWARDS, type],
    queryFn: getTodaysBonusRewards,
    select: (data) => {
      const filteredResults =
        type === RewardsTodayType.DEDUCTION
          ? data.results.filter((reward: any) => reward.coins < 0)
          : data.results.filter((reward: any) => reward.coins > 0)
      return filteredResults.map((reward: any) => reward.receiver_id)
    },
    enabled: !!family,
  })
}
